import React from 'react';
import classNames from 'classnames';
import { ReactComponent as HandshakeIcon } from '../icons/prequal_handshake.svg';
import { ReactComponent as MedalIcon } from '../icons/prequal_medal.svg';
import { ReactComponent as ArrowRightIcon } from '../icons/prequal_v2_arrow_right.svg';
import { ReactComponent as BackgroundShapeIcon } from '../icons/prequal_v2_background_shape.svg';
import { AppFooter } from '../loan/components/AppFooter/AppFooter';
import { PUBLIC_URL } from '../constants';
import {
  PREQUAL_LANDING_FAQ_HREF,
  PREQUAL_LANDING_PHONE_DISPLAY,
  PREQUAL_LANDING_PHONE_HREF,
  PREQUAL_LANDING_STATIC_SOURCE,
  trackPreQualLandingCtaClick,
  trackPreQualLandingFaqClick,
  trackPreQualLandingPhoneClick,
  usePreQualLandingAutoAdvance,
} from './PreQualLandingPage';
import styles from './PreQualLandingPageV2.module.scss';

type Props = {
  onGetPreQualified: () => void;
};

const PreQualLandingPageV2: React.FC<Props> = ({ onGetPreQualified }) => {
  const { hideChrome, shouldAutoAdvance } = usePreQualLandingAutoAdvance(onGetPreQualified);

  const handleCtaClick = (): void => {
    trackPreQualLandingCtaClick();
    onGetPreQualified();
  };

  // Renders nothing on the very first pass so there is no landing-page
  // content to flash before handoff — the redirect itself fires from
  // usePreQualLandingAutoAdvance's useLayoutEffect, after this (already-empty)
  // paint.
  if (shouldAutoAdvance) {
    return null;
  }

  return (
    <div className={styles.landingRoot} data-testid="prequal-landing-page-v2">
      {!hideChrome && (
        <header className={styles.header}>
          <img
            alt="Boat Trader | Trident Funding"
            className={styles.headerLogo}
            src={`${PUBLIC_URL}/static/trident/boattrader-trident-logo-blue.png`}
          />
        </header>
      )}

      <section
        className={classNames(styles.heroSection, { [styles.heroSectionHideChrome]: hideChrome })}
        data-testid="prequal-landing-hero"
      >
        <BackgroundShapeIcon aria-hidden="true" className={styles.backgroundShape} />

        <div className={styles.heroContent}>
          <h1 className={styles.heroHeading}>
            Get <span className={styles.heroHeadingAccent}>Prequalified</span> for a Boat Loan
          </h1>
          <p className={styles.heroSubheading}>
            Pre-qualification takes 3 minutes, uses a soft credit check and gives you real approval
            odds, so you can shop with confidence.
          </p>

          <button
            className={styles.ctaButton}
            data-testid="prequal-landing-v2-cta"
            onClick={handleCtaClick}
            type="button"
          >
            Get pre-qualified
            <ArrowRightIcon aria-hidden="true" className={styles.ctaButtonIcon} />
          </button>
        </div>
      </section>

      <section className={styles.whySection}>
        <div className={styles.whyInner}>
          <div className={styles.whyContent}>
            <div className={styles.eyebrow}>
              <span className={styles.eyebrowLine} />
              WHY IT MATTERS
            </div>
            <h2 className={styles.whyHeading}>Shop smarter, not slower</h2>
            <p className={styles.whySubheading}>
              A quick pre-qual shows you exactly what you can afford.
              <br />
              No obligation, no surprises.
            </p>
          </div>

          <div className={styles.cardsAndMockupGroup}>
            <div className={styles.cardsAndMockup}>
              <div className={styles.cardColumn}>
                <div className={styles.card}>
                  <div className={styles.cardIcon}>
                    <MedalIcon />
                  </div>
                  <h3 className={styles.cardTitle} data-testid="prequal-landing-v2-card-title">
                    No credit impact
                  </h3>
                  <p className={styles.cardText}>
                    A soft inquiry only, our rate check won&apos;t affect your credit score in any
                    way.
                  </p>
                </div>

                <div className={styles.cardDivider} />

                <div className={styles.card}>
                  <div className={styles.cardIcon}>
                    <HandshakeIcon />
                  </div>
                  <h3 className={styles.cardTitle} data-testid="prequal-landing-v2-card-title">
                    Expert-Led
                  </h3>
                  <p className={styles.cardText}>
                    Marine finance specialists guide you from pre-qual through closing.
                  </p>
                </div>
              </div>

              <div aria-hidden="true" className={styles.phoneMockupWrapper}>
                <img
                  alt=""
                  className={styles.phoneMockupImg}
                  src={`${PUBLIC_URL}/static/trident/prequal-v2-results-excellent-mockup.png`}
                />
              </div>
            </div>

            <div aria-hidden="true" className={styles.sectionDivider} />
          </div>
        </div>

        <div className={styles.questionsBlock}>
          <span className={styles.questionsPrompt}>Questions?</span>
          <span className={styles.questionsInfo}>
            Visit our{' '}
            <a
              className={styles.questionsLink}
              data-testid="prequal-landing-v2-faq-link"
              href={PREQUAL_LANDING_FAQ_HREF}
              onClick={trackPreQualLandingFaqClick}
              rel="noopener noreferrer"
              target="_blank"
            >
              FAQ
            </a>{' '}
            or call{' '}
            <a
              className={styles.questionsLink}
              data-testid="prequal-landing-v2-phone-link"
              href={PREQUAL_LANDING_PHONE_HREF}
              onClick={trackPreQualLandingPhoneClick}
            >
              {PREQUAL_LANDING_PHONE_DISPLAY}
            </a>
          </span>
        </div>
      </section>

      {!hideChrome && <AppFooter sourceOverride={PREQUAL_LANDING_STATIC_SOURCE} />}
    </div>
  );
};

export default PreQualLandingPageV2;
